import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["match", "matched", "onMatchedChange", "size", "value", "error", "inputRef", "onChange"];
import styled from 'foundations-theming/utils';
import { useEffect, useState, useRef, useCallback, memo } from 'react';
import { Controllable } from '../Controllable';
import ShareInput from '../ShareInput/ShareInput';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { inputCouldMatch } from '../../utils/Strings';
import { getTextHeight } from '../../utils/MeasureText';
import { TextArea } from '../TextArea/TextArea';
import { View } from '../../../components/View';
import { MatchTextAreaContextProvider } from './MatchTextAreaContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PlaceholderText = styled.div.withConfig({
  displayName: "MatchTextArea__PlaceholderText"
})(({
  $size,
  theme
}) => {
  return {
    color: theme.color['icon-core-subtle'],
    position: 'relative',
    wordWrap: 'break-word',
    fontSize: $size === 'xxl' ? theme.typography['heading-600'].fontSize : theme.typography['body-200'].fontSize,
    lineHeight: $size === 'xxl' ? '40px' : theme.typography['body-200'].lineHeight
  };
});
const MatchTextAreaComponent = _ref => {
  let {
      match,
      matched = false,
      onMatchedChange,
      size = 'md',
      value = '',
      error = false,
      inputRef,
      onChange
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [matchTextHeight, setMatchTextHeight] = useState(null);
  const textAreaRef = useRef(null);
  const handleMatchedChange = useCallback(() => {
    const newMatched = value === match;

    // Only call `onMatchedChange` if the matched state has actually changed
    if (newMatched !== matched && onMatchedChange) {
      onMatchedChange(syntheticEvent(newMatched));
    }
  }, [value, match, matched, onMatchedChange]);
  const computeMatchTextHeight = useCallback(() => {
    const inputEl = (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) || textAreaRef.current;
    if (!inputEl) return;
    const originalInputValue = inputEl.value;
    inputEl.value = match;
    const height = getTextHeight(inputEl, null);
    inputEl.value = originalInputValue;
    if (height !== matchTextHeight) {
      setMatchTextHeight(height);
    }
  }, [match, matchTextHeight, inputRef]);
  useEffect(() => {
    computeMatchTextHeight();
  }, [match, computeMatchTextHeight]);
  useEffect(() => {
    handleMatchedChange();
    window.addEventListener('resize', computeMatchTextHeight);
    return () => {
      window.removeEventListener('resize', computeMatchTextHeight);
    };
  }, [match, value, matchTextHeight, inputRef, handleMatchedChange, computeMatchTextHeight]);
  const handleChange = evt => {
    const {
      value: evtValue
    } = evt.target;
    if (!inputCouldMatch(evtValue, match)) {
      return;
    }
    if (onChange) {
      onChange(evt);
    }
    const newMatched = evtValue === match;
    if (newMatched !== matched && onMatchedChange) {
      onMatchedChange(syntheticEvent(newMatched));
    }
  };
  return /*#__PURE__*/_jsx(MatchTextAreaContextProvider, {
    value: {
      isBgTransparent: true,
      size,
      height: matchTextHeight ? `${matchTextHeight}px` : undefined
    },
    children: /*#__PURE__*/_jsxs(View, {
      position: "relative",
      inlineSize: "100%",
      blockSize: "auto",
      children: [/*#__PURE__*/_jsx(View, {
        blockSize: matchTextHeight === null || matchTextHeight === void 0 ? void 0 : matchTextHeight.toString(),
        paddingBlock: size === 'xxl' ? '400' : '200',
        paddingInline: "400",
        position: "relative",
        borderWidth: "100",
        borderStyle: "solid",
        borderColor: "fill-surface-default",
        backgroundColor: "fill-surface-raised",
        children: /*#__PURE__*/_jsx(PlaceholderText, {
          $size: size,
          children: match
        })
      }), /*#__PURE__*/_jsx(View, {
        inlineSize: "100%",
        blockSize: matchTextHeight === null || matchTextHeight === void 0 ? void 0 : matchTextHeight.toString(),
        position: "absolute",
        insetInlineStart: "0",
        insetBlockStart: "0",
        children: /*#__PURE__*/_jsx(TextArea, Object.assign({}, rest, {
          "data-match-value": match,
          value: value,
          onChange: handleChange,
          resize: false,
          padding: size === 'xxl' ? '400' : undefined,
          inputRef: textAreaRef,
          error: error
        }))
      })]
    })
  });
};
const defaultProps = {
  matched: false
};
export const MatchTextArea = ShareInput(Controllable( /*#__PURE__*/memo(MatchTextAreaComponent), ['matched', 'value'], defaultProps));
MatchTextArea.displayName = 'MatchTextArea';